import { DateTime } from 'luxon';
import { zip } from 'ramda';

import { ProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { requestGoogleAccess } from 'core/googleClient';
import { Offer, Option } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';
import { flattenCategories } from 'modules/shared-menu/pages/SharedMenuEditorPage/utils';
import { SelectionCategory } from 'modules/shared-menu/types';

import {
  ShapelessLine,
  categoryModelToCategoryLine,
  decoder,
  offerModelToOfferLine,
  optionModelsToOptionLines,
  lineSpecs,
} from './models';
import { categorySample, offerSample, optionSample } from './sample';
import { CategoryLine, OfferLine, OptionLine } from './types';
import { SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET } from '../../components/DataTable/constants';
import { removeNonMatchingKeysFromObject } from '../../utils';

type SpreadsheetOption = {
  lang: string;
  currency?: string;
  selectedFields?: SheetNameToShapedLinesMap;
  prefilledFields?: Partial<{ offer: Partial<ShapelessLine>; option: Partial<ShapelessLine> }>;
  productHierarchies: ProductInformationSettings['product_hierarchy_levels'];
};

export type WithRowNumber<T> = T & { rowNumber: number };

type SpreadsheetData = {
  readonly options: Partial<OptionLine>[];
  readonly offers: Partial<OfferLine>[];
  readonly categories: Partial<CategoryLine>[];
};

type SpreadsheetCellValuesRequest = {
  spreadsheetId: string;
  ranges: string[];
  valueRenderOption: string;
};

type SheetBasicData = {
  name: keyof SpreadsheetData;
  cellData: (string | number | null | undefined)[][];
  formats?: ColumnFormat[];
  validations?: ColumnValidation[];
  widths?: ColumnWidth[];
};

export type ColumnWidth = {
  columnName: string;
  width: number;
};

export type ColumnFormat = {
  columnName: string;
  format: string;
  type: 'NUMBER' | 'CURRENCY';
};

export type ColumnValidation = {
  columnName: string;
  rule: ValidationRule;
};

export type ValidationRule = gapi.client.sheets.DataValidationRule;

export type DownloadableSpreadsheet = {
  downloadOffers: () => Promise<WithRowNumber<Partial<OfferLine>>[]>;
  downloadOptions: () => Promise<WithRowNumber<Partial<OptionLine>>[]>;
  downloadCategories: () => Promise<WithRowNumber<Partial<CategoryLine>>[]>;
  spreadsheet: gapi.client.sheets.Spreadsheet;
};

export type SheetNameToShapedLinesMap = {
  offers: (keyof OfferLine)[];
  categories: (keyof CategoryLine)[];
  options: (keyof OptionLine)[];
};

const emptySpreadsheet = (): SpreadsheetData => ({
  offers: [],
  options: [],
  categories: [],
});

type SpreadsheetArgs = {
  spreadsheetOptions: SpreadsheetOption;
};

const PRODUCT_FAMILY_ID_LENGTH = 9;

export const spreadsheet = ({ spreadsheetOptions }: SpreadsheetArgs) => {
  const { selectedFields } = spreadsheetOptions;

  const spreadsheetInnerState = (data: SpreadsheetData) => ({
    data,
    selectFile: () => selectGoogleSpreadsheet(),
    export: () => {
      const spreadsheetName = `export-${DateTime.local().toISODate()}:${DateTime.local().toISOTime()}`;

      const offersOnlyWithSelectedFields: ShapelessLine[] = data.offers.map(offer =>
        removeNonMatchingKeysFromObject(offer, selectedFields?.offers),
      );

      const optionsOnlyWithSelectedFields: ShapelessLine[] = data.options.map(option =>
        removeNonMatchingKeysFromObject(option, selectedFields?.options),
      );

      const categoriesOnlyWithSelectedFields: ShapelessLine[] = data.categories.map(category =>
        removeNonMatchingKeysFromObject(category, selectedFields?.categories),
      );

      const offers = toSheet('offers', offersOnlyWithSelectedFields);
      const options = toSheet('options', optionsOnlyWithSelectedFields);
      const categories = toSheet('categories', categoriesOnlyWithSelectedFields);

      return uploadToGoogleSheets(spreadsheetName, offers, options, categories);
    },

    find: async (spreadsheetId: string) => {
      const spreadsheet = await fetchGoogleSpreadsheetInfo(spreadsheetId);
      return sheetCellDataFetcher({
        spreadsheet,
        currency: spreadsheetOptions.currency,
        productHierarchies: spreadsheetOptions.productHierarchies,
      });
    },

    select: async () => {
      const file = await selectGoogleSpreadsheet();
      const spreadsheet = file?.id ? await fetchGoogleSpreadsheetInfo(file.id) : null;
      return spreadsheet
        ? sheetCellDataFetcher({
            spreadsheet,
            currency: spreadsheetOptions.currency,
            preFilledFields: spreadsheetOptions.prefilledFields,
            productHierarchies: spreadsheetOptions.productHierarchies,
          })
        : null;
    },

    attachOptions: (options: Option[]) =>
      spreadsheetInnerState({
        ...data,
        options: optionModelsToOptionLines(options, spreadsheetOptions.lang),
      }),

    attachOffers: (offers: Offer[]) =>
      spreadsheetInnerState({
        ...data,
        offers: offers.map(offer =>
          offerModelToOfferLine(offer, spreadsheetOptions.lang, spreadsheetOptions.productHierarchies),
        ),
      }),

    attachCategories: (categories: SelectionCategory[]) =>
      spreadsheetInnerState({
        ...data,
        categories: flattenCategories(categories).map(cat => categoryModelToCategoryLine(cat, spreadsheetOptions.lang)),
      }),

    notEmptyOrSampleSpreadsheet: () =>
      data.options.length === 0 && data.offers.length === 0 && data.categories.length === 0
        ? spreadsheetInnerState({ ...data })
            .attachOffers(offerSample)
            .attachCategories(categorySample)
            .attachOptions(optionSample)
        : spreadsheetInnerState({ ...data }),
  });
  return spreadsheetInnerState(emptySpreadsheet());
};

type SheetCellDataFetcherArgs = {
  spreadsheet: gapi.client.sheets.Spreadsheet;
  currency?: string;
  preFilledFields?: SpreadsheetOption['prefilledFields'];
  productHierarchies: ProductInformationSettings['product_hierarchy_levels'];
};

const sheetCellDataFetcher = async ({
  spreadsheet,
  currency,
  preFilledFields,
  productHierarchies,
}: SheetCellDataFetcherArgs) => {
  const decode = decoder(currency);
  const gapi = await requestGoogleAccess();
  const fetchSheetsData = async (sheetName: keyof SpreadsheetData) => {
    const spreadsheetId = spreadsheet.spreadsheetId ?? '';
    const grid = gridProperties(findSheet(spreadsheet, sheetName));
    const ranges = grid ? [buildRange(grid.columnCount, grid.rowCount, sheetName)] : [];
    const valueRenderOption = 'UNFORMATTED_VALUE';
    const request = { spreadsheetId, ranges, valueRenderOption };
    return fetchGoogleSpreadsheetCellValues(gapi, request);
  };

  return {
    spreadsheet,
    downloadOffers: (): Promise<WithRowNumber<Partial<OfferLine>>[]> =>
      fetchSheetsData('offers').then(lines =>
        tabularDataToRecord(lines).map((line, index) => {
          const isOfferCreation = !line.id;
          const offerLine = isOfferCreation
            ? decode.offerLine({ ...preFilledFields?.offer, ...line })
            : decode.offerLine(line);
          const formattedProductFamilyId = formatProductFamilyId(offerLine.product_family_id);
          return {
            ...offerLine,
            product_family_id: formattedProductFamilyId,
            product_hierarchy_level_id: getProductHierarchyLevelId({
              productFamilyId: formattedProductFamilyId,
              productHierarchies,
            }),
            rowNumber: index + SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET,
          };
        }),
      ),
    downloadOptions: (): Promise<WithRowNumber<Partial<OptionLine>>[]> =>
      fetchSheetsData('options').then(lines =>
        tabularDataToRecord(lines)
          .map(line =>
            line.type === 'multi_choice' ? fillDefaultOptionValues(preFilledFields?.option ?? {}, line) : line,
          )
          .map(decode.optionLine)
          .map((line, index) => ({ ...line, rowNumber: index + SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET })),
      ),
    downloadCategories: (): Promise<WithRowNumber<Partial<CategoryLine>>[]> =>
      fetchSheetsData('categories').then(lines =>
        tabularDataToRecord(lines)
          .map(decode.categoryLine)
          .map((line, index) => ({ ...line, rowNumber: index + SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET })),
      ),
  };
};

const selectGoogleSpreadsheet = async (): Promise<google.picker.DocumentObject | null> => {
  const gapi = await requestGoogleAccess();
  return new Promise(resolve => {
    const callback = (data: google.picker.ResponseObject) => {
      if (data.action === google.picker.Action.PICKED && data.docs[0]) {
        resolve(data.docs[0]);
      } else if (data.action === google.picker.Action.CANCEL) {
        resolve(null);
      }
    };
    const picker = new google.picker.PickerBuilder()
      .addView(google.picker.ViewId.SPREADSHEETS)
      .disableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(gapi.client.getToken().access_token)
      .setCallback(callback)
      .build();
    picker.setVisible(true);
  });
};

const fetchGoogleSpreadsheetInfo = async (spreadsheetId: string) => {
  const gapi = await requestGoogleAccess();
  return gapi.client.sheets.spreadsheets.get({ spreadsheetId }).then(response => response.result);
};

const fetchGoogleSpreadsheetCellValues = (client: typeof gapi, request: SpreadsheetCellValuesRequest) =>
  client.client.sheets.spreadsheets.values
    .batchGet(request)
    .then(response => response.result.valueRanges?.[0]?.values || []);

const uploadToGoogleSheets = async (spreadsheetName: string, ...sheetDatas: SheetBasicData[]) => {
  try {
    const spreadsheetId = await createSpreadsheet(spreadsheetName);
    const spreadsheetUpdated = await overwriteSpreadsheet(spreadsheetId, ...sheetDatas);
    return spreadsheetUpdated;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const createSpreadsheet = async (spreadsheetName: string) => {
  const gapi = await requestGoogleAccess();
  return await gapi.client.sheets.spreadsheets
    .create({}, { properties: { title: spreadsheetName } })
    .then(response => response.result.spreadsheetId ?? '');
};

const overwriteSpreadsheet = async (spreadsheetId: string, ...sheetDatas: SheetBasicData[]) => {
  // sheetId can be specified; note: 0 is reserved for the sheet google creates by default
  const gapi = await requestGoogleAccess();
  const requests = sheetDatas
    .filter(sheetDatas => sheetDatas.cellData.length > 0)
    .flatMap((sheetData, index) => addSheet(sheetData, index + 1));
  return await gapi.client.sheets.spreadsheets
    .batchUpdate({ spreadsheetId, resource: { requests, includeSpreadsheetInResponse: true } })
    .then(response => response.result.updatedSpreadsheet);
};

const addSheet = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] => [
  // first we attach the data
  addSheetRequest(sheet, sheetId),
  updateCellsRequest(sheet, sheetId),
  // default style
  defaultHeader(sheetId),
  defaultGlobalStyle(sheet, sheetId),
  fitToWidth(sheet, sheetId),
  // then style infered from line spec
  ...validationRequestFromSpec(sheet, sheetId),
  // then style specific to the spreadsheet
  ...sheetSpecificStyleRequest(sheet, sheetId),
  // then user style (to be implemented when needed)
];

const addSheetRequest = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request => ({
  addSheet: {
    properties: {
      title: sheet.name,
      sheetId,
      index: 0,
      gridProperties: {
        rowCount: sheet.cellData.length,
        columnCount: sheet.cellData[0].length,
        frozenRowCount: sheet.cellData.length > 0 ? 1 : 0,
      },
    },
  },
});

const updateCellsRequest = (sheet: SheetBasicData, sheetId: number) => ({
  updateCells: {
    rows: updateCellsRequestRow(sheet),
    range: updateCellsRequestRange(sheet, sheetId),
    fields: 'userEnteredValue, userEnteredFormat(verticalAlignment, padding, wrapStrategy)',
  },
});

const updateCellsRequestRow = (sheet: SheetBasicData) =>
  sheet.cellData.map(row => ({
    values: row.map(value => ({
      userEnteredValue: userValue(value),
      userEnteredFormat: {
        verticalAlignment: 'TOP',
        padding: {
          left: 10,
          right: 5,
        },
        wrapStrategy: 'CLIP',
      },
    })),
  }));

const updateCellsRequestRange = (sheet: SheetBasicData, sheetId: number) => ({
  sheetId,
  startRowIndex: 0,
  endRowIndex: sheet.cellData.length,
  startColumnIndex: 0,
  endColumnIndex: sheet.cellData[0].length,
});

const validationRequestFromSpec = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] =>
  sheet.name === 'options'
    ? []
    : validationFromSpec(sheet).flatMap(rule => validationRuleToGoogleRequest(sheet, sheetId, rule));

const sheetSpecificStyleRequest = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] => {
  const handler =
    {
      offers: addOffersSheetStyle,
      options: addOptionsSheetStyle,
      categories: addCategoriesSheetStyle,
    }[sheet.name] ?? ((..._: any) => []);
  return handler(sheet, sheetId);
};

const userValue = (value: number | string | null | boolean | undefined) => {
  const userValue = value === null ? '' : value;
  const valueType = typeof userValue;
  return valueType === 'number'
    ? { numberValue: value as number }
    : valueType === 'boolean'
      ? { boolValue: value as boolean }
      : { stringValue: value as string };
};

const rangeSizeToColumnName = (size: number): string => {
  if (size <= 0) {
    return '';
  }
  // basically doing a base 26 encoding apart from:
  // 0 is the empty char and we start counting form 'A'
  const encoding = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const remaining = (size - 1) % encoding.length;
  const divider = Math.floor((size - 1) / encoding.length);
  const lastChar = encoding[remaining];
  return divider === 0 ? lastChar : rangeSizeToColumnName(divider) + lastChar;
};

const buildRange = (columnCount: number, rowCount: number, sheetName: string = 'Sheet1'): string =>
  `${sheetName}!A1:${rangeSizeToColumnName(columnCount)}${rowCount}`;

const findSheet = (spreadsheet: gapi.client.sheets.Spreadsheet, sheetName: string) =>
  spreadsheet.sheets?.find(sheet => sheet.properties?.title === sheetName);

const gridProperties = (sheet: gapi.client.sheets.Sheet | undefined) =>
  sheet?.properties?.gridProperties?.columnCount && sheet?.properties?.gridProperties?.rowCount
    ? (sheet.properties.gridProperties as { columnCount: number; rowCount: number }) // correctness by above condition
    : undefined;

const tabularDataToRecord = (linesAsArray: any[][]): ShapelessLine[] => {
  const [columns, ...values] = linesAsArray!;
  return values.map(row => Object.fromEntries(zip(columns, Array.from({ ...row, length: columns.length }))));
};

const fillDefaultOptionValues = (
  prefilledFields: Partial<OptionLine>,
  line: Partial<OptionLine>,
): Partial<OptionLine> => {
  return Object.fromEntries(
    Object.entries(line).map(([key, value]: [keyof OptionLine, string]) => {
      if (!value && Object.keys(prefilledFields).includes(key)) {
        return [key, prefilledFields[key]];
      }
      return [key, value];
    }),
  );
};

const toSheet = (
  name: keyof SpreadsheetData,
  records: ShapelessLine[],
  formats?: ColumnFormat[],
  validations?: ColumnValidation[],
  widths?: ColumnWidth[],
): SheetBasicData => {
  const cellData = recordsToTabularData(records);
  return { name, cellData, formats, validations, widths };
};

const validationFromSpec = (sheet: SheetBasicData): ColumnValidation[] =>
  lineSpecs[sheet.name].booleanFields.map(columnName => ({ columnName, rule: boolChoice }));

const columnIndex = (name: string, sheet: SheetBasicData) =>
  sheet.cellData?.[0].findIndex(column => column === name) ?? -1;

const recordsToTabularData = (records: ShapelessLine[]): (string | number | null | undefined)[][] => {
  const columns = collectAllColumnNames(records);
  const flatLines = records.map(record => recordToFlatLine(columns, record));
  return columns.length > 0 ? [columns as (string | number | null | undefined)[]].concat(flatLines) : [];
};

const collectAllColumnNames = (records: ShapelessLine[]): string[] => {
  return records
    .flatMap(record => Object.keys(record))
    .reduce((acc: string[], columnName: string) => (acc.includes(columnName) ? acc : [...acc, columnName]), []);
};

const recordToFlatLine = (columns: string[], record: ShapelessLine) => columns.map(columnName => record[columnName]);

/**
 * Styling spreadsheet below
 */
const defaultGlobalStyle: (d: SheetBasicData, s: number) => Pick<gapi.client.sheets.Request, 'repeatCell'> = (
  sheet: SheetBasicData,
  sheetId: number,
) => ({
  repeatCell: {
    range: { sheetId, startRowIndex: 0, endRowIndex: sheet.cellData.length },
    cell: {
      userEnteredFormat: {
        textFormat: {
          bold: false,
          fontFamily: 'Calibri',
          fontSize: 10,
        },
      },
    },
    fields: 'userEnteredFormat(textFormat)',
  },
});

const defaultHeader: (s: number) => Pick<gapi.client.sheets.Request, 'repeatCell'> = (sheetId: number) => ({
  repeatCell: {
    range: { sheetId, startRowIndex: 0, endRowIndex: 1 },
    cell: {
      userEnteredFormat: {
        wrapStrategy: 'CLIP',
        verticalAlignment: 'TOP',
        horizontalAlignment: 'CENTER',
        padding: { left: 10, right: 10, top: 10, bottom: 10 },
        backgroundColor: { red: 0.7450980392, green: 0.8862745098039215, blue: 0.9803921568627451 },
        textFormat: {
          bold: false,
          fontSize: 12,
          fontFamily: 'Calibri',
        },
      },
    },
    fields:
      'userEnteredFormat(backgroundColor,textFormat,horizontalAlignment, verticalAlignment, padding, wrapStrategy)',
  },
});

const fitToWidth: (sheet: SheetBasicData, id: number) => Pick<gapi.client.sheets.Request, 'autoResizeDimensions'> = (
  sheet: SheetBasicData,
  sheetId: number,
) => ({
  autoResizeDimensions: {
    dimensions: {
      sheetId,
      dimension: 'COLUMNS',
      startIndex: 0,
      endIndex: sheet.cellData[0].length,
    },
  },
});

const addOffersSheetStyle = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] => [
  ...validationRuleToGoogleRequest(sheet, sheetId, { columnName: 'caffeine_content_units', rule: caffeineUnitsChoice }),
];

const validationRuleToGoogleRequest = (
  sheet: SheetBasicData,
  sheetId: number,
  rule: ColumnValidation,
): gapi.client.sheets.Request[] => {
  const index = columnIndex(rule.columnName, sheet);
  return index >= 0
    ? [
        {
          setDataValidation: {
            range: {
              sheetId,
              startRowIndex: 1,
              startColumnIndex: index,
              endColumnIndex: index + 1,
            },
            rule: rule.rule,
          },
        },
      ]
    : [];
};

const addOptionsSheetStyle = (sheet: SheetBasicData, sheetId: number): gapi.client.sheets.Request[] =>
  sheet.cellData.slice(1).flatMap((line, index) => {
    if (isLineEmpty(line)) {
      return [
        {
          repeatCell: {
            range: { sheetId, startRowIndex: 1 + index, endRowIndex: 2 + index },
            cell: {
              userEnteredFormat: { backgroundColor: { red: 0.95, green: 0.95, blue: 0.95 } },
            },
            fields: 'userEnteredFormat(backgroundColor)',
          },
        },
        {
          setDataValidation: {
            range: {
              sheetId,
              startRowIndex: 1 + index,
              endRowIndex: 2 + index,
              startColumnIndex: 0,
              endColumnIndex: sheet.cellData[0].length,
            },
            rule: emptyChoice,
          },
        },
      ];
    } else {
      return [
        ...lineSpecs[sheet.name].booleanFields
          .filter(column => {
            /**
             * lineSpecs.options.booleanFields assumes that all boolean fields exist in the spreadsheet
             * but if the user unselected any of them before exporting, the `colIndex` will be -1 (not found in the spreadsheet)
             * this will cause the spreadsheet creation to fail
             * so, we filter out the columns that are not found in the spreadsheet before mapping them to the boolean fields validation rules
             */
            const colIndex = columnIndex(column, sheet);
            return !!(colIndex > 0);
          })
          .map(column => {
            const colIndex = columnIndex(column, sheet);
            return {
              setDataValidation: {
                range: {
                  sheetId,
                  startRowIndex: 1 + index,
                  endRowIndex: 2 + index,
                  startColumnIndex: colIndex,
                  endColumnIndex: colIndex + 1,
                },
                rule: boolChoice,
              },
            };
          }),
      ];
    }
  });

const addCategoriesSheetStyle = (_: SheetBasicData, __: number): gapi.client.sheets.Request[] => [];

const boolChoice: ValidationRule = {
  condition: {
    type: 'BOOLEAN',
    values: [{ userEnteredValue: 'TRUE' }, { userEnteredValue: 'FALSE' }],
  },
  strict: true,
  showCustomUi: true,
};

const emptyChoice: ValidationRule = {
  condition: {
    type: 'TEXT_EQ',
    values: [{ userEnteredValue: '' }],
  },
  strict: false,
};

const caffeineUnitsChoice: ValidationRule = {
  condition: {
    type: 'ONE_OF_LIST',
    values: [{ userEnteredValue: 'mg per 100 ml' }, { userEnteredValue: 'mg per 100 g' }],
  },
  strict: true,
  showCustomUi: true,
};

const isLineEmpty = (line: any[]) => line.filter(value => !!value).length === 0;

const formatProductFamilyId = (productFamilyId?: string) =>
  productFamilyId?.toString().padStart(PRODUCT_FAMILY_ID_LENGTH, '0');

const getProductHierarchyLevelId = ({
  productFamilyId,
  productHierarchies,
}: {
  productFamilyId?: string;
  productHierarchies: ProductInformationSettings['product_hierarchy_levels'];
}) =>
  productFamilyId
    ? productHierarchies.find(hierarchy => hierarchy.human_readable_id === productFamilyId)?.id
    : undefined;
